
import {
  CATALOG_IMAGE_LIMITS,
  buildCatalogProductPatch,
  catalogProductPriceChanged,
  extractCatalogCollection,
  normalizeCatalogPagination,
  normalizeCatalogProduct,
  normalizeDecimal,
  sanitizeCatalogPriceInput,
  shouldRemoveCatalogMainImage,
  validateCatalogImageDimensions,
  validateCatalogProductPrice,
} from "../catalogoDigitalConfig";
import {
  getCatalogManagementProducts,
  publishCatalogProducts,
} from "../services/catalogoDigitalApi";
import CatalogImageCropModal from "./CatalogImageCropModal.vue";

const DEFAULT_META = Object.freeze({
  current_page: 1,
  last_page: 1,
  per_page: 12,
  total: 0,
  from: null,
  to: null,
});
const MAX_PRODUCT_PRICE = 999999999.99;
const COMPLEMENTARY_SLOTS = Object.freeze([1, 2]);

export default {
  name: "CatalogoDigitalProductsTab",
  components: { CatalogImageCropModal },
  data() {
    return {
      products: [],
      drafts: {},
      meta: { ...DEFAULT_META },
      searchInput: "",
      appliedSearch: "",
      loading: false,
      saving: false,
      searchTimer: null,
      requestSequence: 0,
      allowRouteLeave: false,
      complementarySlots: COMPLEMENTARY_SLOTS,
      validatingImages: 0,
      componentDestroyed: false,
      cropSelection: null,
    };
  },
  computed: {
    dirtyCount() {
      return Object.keys(this.drafts).length;
    },
    hasDrafts() {
      return this.dirtyCount > 0;
    },
    imageValidationBusy() {
      return this.validatingImages > 0 || Boolean(this.cropSelection);
    },
    visiblePages() {
      const last = this.meta.last_page;
      const current = this.meta.current_page;
      const start = Math.max(1, Math.min(current - 2, last - 4));
      const end = Math.min(last, start + 4);
      const pages = [];
      for (let page = start; page <= end; page += 1) pages.push(page);
      return pages;
    },
  },
  created() {
    this.loadProducts();
    window.addEventListener("focus", this.handleWindowFocus);
    window.addEventListener("beforeunload", this.handleBeforeUnload);
  },
  beforeDestroy() {
    this.componentDestroyed = true;
    this.requestSequence += 1;
    window.removeEventListener("focus", this.handleWindowFocus);
    window.removeEventListener("beforeunload", this.handleBeforeUnload);
    if (this.searchTimer) window.clearTimeout(this.searchTimer);
    Object.keys(this.drafts).forEach((id) => this.revokeAllDraftPreviews(id));
  },
  methods: {
    async loadProducts(options = {}) {
      const requestId = ++this.requestSequence;
      if (!options.silent) this.loading = true;
      try {
        const data = await getCatalogManagementProducts({
          page: this.meta.current_page,
          per_page: this.meta.per_page,
          busca: this.appliedSearch,
          publicado: "todos",
        });
        if (requestId !== this.requestSequence) return;
        this.products = extractCatalogCollection(data, "produtos").map(
          normalizeCatalogProduct
        );
        this.meta = normalizeCatalogPagination(
          (data && data.meta) || data || {},
          this.meta
        );
      } catch (error) {
        if (requestId !== this.requestSequence) return;
        this.notifyError(error, "Não foi possível carregar os produtos.");
      } finally {
        if (requestId === this.requestSequence) this.loading = false;
      }
    },
    handleWindowFocus() {
      if (!this.hasDrafts && !this.saving && !this.imageValidationBusy) {
        this.loadProducts({ silent: true });
      }
    },
    handleBeforeUnload(event) {
      if ((!this.hasDrafts && !this.imageValidationBusy) || this.saving) return;
      event.preventDefault();
      event.returnValue = "";
    },
    scheduleSearch() {
      if (this.saving || this.imageValidationBusy) return;
      if (this.searchTimer) window.clearTimeout(this.searchTimer);
      this.searchTimer = window.setTimeout(this.applySearch, 350);
    },
    applySearch() {
      if (this.searchTimer) window.clearTimeout(this.searchTimer);
      this.searchTimer = null;
      if (this.saving || this.imageValidationBusy) return;
      const nextSearch = this.searchInput.trim();
      if (nextSearch === this.appliedSearch && this.meta.current_page === 1) {
        return;
      }
      this.appliedSearch = nextSearch;
      this.meta.current_page = 1;
      this.loadProducts();
    },
    clearSearch() {
      if (this.saving || this.imageValidationBusy) return;
      this.searchInput = "";
      this.applySearch();
    },
    goToPage(page) {
      if (this.saving || this.imageValidationBusy) return;
      const nextPage = Math.max(1, Math.min(Number(page), this.meta.last_page));
      if (nextPage === this.meta.current_page || this.loading) return;
      this.meta.current_page = nextPage;
      this.loadProducts();
    },
    draftFor(product) {
      return this.drafts[String(product.id)] || null;
    },
    complementaryProductImage(product, slot) {
      const images = Array.isArray(product.imagens_complementares)
        ? product.imagens_complementares
        : [];
      return (
        images.find((image) => Number(image.ordem) === Number(slot)) || null
      );
    },
    buildComplementaryDraft(product) {
      return COMPLEMENTARY_SLOTS.reduce((slots, slot) => {
        const original = this.complementaryProductImage(product, slot);
        slots[String(slot)] = {
          original_url: (original && original.imagem_url) || "",
          image_file: null,
          image_preview: "",
          image_crop: null,
          removed: false,
        };
        return slots;
      }, {});
    },
    ensureDraft(product) {
      const key = String(product.id);
      if (!this.drafts[key]) {
        this.$set(this.drafts, key, {
          id: Number(product.id),
          catalogo_digital: Boolean(product.catalogo_digital),
          original_catalogo_digital: Boolean(product.catalogo_digital),
          original_preco: normalizeDecimal(product.valor_venda) || "0.00",
          preco_input: this.formatPriceInput(product.valor_venda),
          preco_erro: "",
          original_image: product.imagem || "",
          image_file: null,
          image_preview: "",
          image_crop: null,
          remover_imagem: false,
          complementary_images: this.buildComplementaryDraft(product),
          remover_imagens_complementares: [],
        });
      }
      return this.drafts[key];
    },
    productPublished(product) {
      const draft = this.draftFor(product);
      return draft
        ? Boolean(draft.catalogo_digital)
        : Boolean(product.catalogo_digital);
    },
    productImage(product) {
      const draft = this.draftFor(product);
      if (!draft) return product.imagem || "";
      if (draft.image_preview) return draft.image_preview;
      if (draft.remover_imagem) return "";
      return product.imagem || draft.original_image || "";
    },
    hasImageDraft(product) {
      const draft = this.draftFor(product);
      return Boolean(
        draft &&
          (draft.image_file || draft.image_preview || draft.remover_imagem)
      );
    },
    complementaryImageState(product, slot) {
      const draft = this.draftFor(product);
      return (
        (draft &&
          draft.complementary_images &&
          draft.complementary_images[String(slot)]) ||
        null
      );
    },
    complementaryImage(product, slot) {
      const state = this.complementaryImageState(product, slot);
      if (state) {
        if (state.image_preview) return state.image_preview;
        if (state.removed) return "";
        return state.original_url || "";
      }

      const original = this.complementaryProductImage(product, slot);
      return (original && original.imagem_url) || "";
    },
    hasComplementaryImageDraft(product, slot) {
      const state = this.complementaryImageState(product, slot);
      return Boolean(state && (state.image_file || state.removed));
    },
    nextComplementarySlot(product) {
      return (
        COMPLEMENTARY_SLOTS.find(
          (slot) => !this.complementaryImage(product, slot)
        ) || null
      );
    },
    canAddComplementaryImage(product) {
      return Boolean(this.nextComplementarySlot(product));
    },
    formatPriceInput(value) {
      const normalized = normalizeDecimal(value);
      if (!normalized) return "";
      return Number(normalized).toLocaleString("pt-BR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    productPriceInput(product) {
      const draft = this.draftFor(product);
      return draft
        ? draft.preco_input
        : this.formatPriceInput(product.valor_venda);
    },
    productPriceError(product) {
      const draft = this.draftFor(product);
      return (draft && draft.preco_erro) || "";
    },
    validateProductPrice(draft) {
      const { normalized, error } = validateCatalogProductPrice(
        draft.preco_input,
        MAX_PRODUCT_PRICE
      );
      this.$set(draft, "preco_erro", error);
      return normalized;
    },
    draftPriceChanged(draft) {
      return catalogProductPriceChanged(draft);
    },
    updateProductPrice(product, value) {
      if (this.saving) return;
      const draft = this.ensureDraft(product);
      draft.preco_input = sanitizeCatalogPriceInput(value);
      this.validateProductPrice(draft);
      this.cleanDraftIfUnchanged(product);
    },
    normalizeProductPrice(product) {
      if (this.saving) return;
      const draft = this.draftFor(product);
      if (!draft) return;
      const normalized = this.validateProductPrice(draft);
      if (normalized) draft.preco_input = this.formatPriceInput(normalized);
      this.cleanDraftIfUnchanged(product);
    },
    toggleProduct(product, value) {
      if (this.saving) return;
      const draft = this.ensureDraft(product);
      draft.catalogo_digital = Boolean(value);
      this.cleanDraftIfUnchanged(product);
    },
    async readCatalogImageDimensions(file) {
      if (typeof window.createImageBitmap === "function") {
        let bitmap = null;
        try {
          bitmap = await window.createImageBitmap(file);
          return { width: bitmap.width, height: bitmap.height };
        } finally {
          if (bitmap && typeof bitmap.close === "function") bitmap.close();
        }
      }

      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = () => reject(new Error("image-read-failed"));
        reader.onload = () => {
          const image = new Image();
          image.onerror = () => reject(new Error("image-decode-failed"));
          image.onload = () =>
            resolve({ width: image.naturalWidth, height: image.naturalHeight });
          image.src = reader.result;
        };
        reader.readAsDataURL(file);
      });
    },
    async validateCatalogImage(file) {
      if (!/^image\/(png|jpeg|webp)$/.test(file.type)) {
        this.$toast.error("Escolha uma imagem PNG, JPG ou WebP.");
        return false;
      }
      if (file.size > CATALOG_IMAGE_LIMITS.max_bytes) {
        this.$toast.error("A imagem deve ter no máximo 5 MB.");
        return false;
      }

      this.validatingImages += 1;
      try {
        const dimensions = await this.readCatalogImageDimensions(file);
        const result = validateCatalogImageDimensions(
          dimensions.width,
          dimensions.height
        );
        if (!result.valid && !this.componentDestroyed) {
          this.$toast.error(result.error);
        }
        return result.valid;
      } catch (error) {
        if (!this.componentDestroyed) {
          this.$toast.error(
            "Não foi possível ler a imagem. Escolha outro arquivo PNG, JPG ou WebP."
          );
        }
        return false;
      } finally {
        this.validatingImages = Math.max(0, this.validatingImages - 1);
      }
    },
    async selectProductImage(product, event) {
      if (this.saving || this.imageValidationBusy) return;
      const file = event.target.files && event.target.files[0];
      event.target.value = "";
      if (!file) return;
      if (!(await this.validateCatalogImage(file))) return;
      if (this.componentDestroyed || this.saving) return;

      this.cropSelection = { product, slot: 0, file };
    },
    async selectNextComplementaryImage(product, event) {
      if (this.saving || this.imageValidationBusy) return;
      const file = event.target.files && event.target.files[0];
      event.target.value = "";
      if (!file) return;

      if (!(await this.validateCatalogImage(file))) return;
      if (this.componentDestroyed || this.saving) return;
      const slot = this.nextComplementarySlot(product);
      if (!slot) {
        this.$toast.error("Cada produto pode ter no máximo três fotos.");
        return;
      }
      this.cropSelection = { product, slot, file };
    },
    async selectComplementaryImage(product, slot, event) {
      if (this.saving || this.imageValidationBusy) return;
      const file = event.target.files && event.target.files[0];
      event.target.value = "";
      if (!file || !(await this.validateCatalogImage(file))) return;
      if (this.componentDestroyed || this.saving) return;
      this.cropSelection = { product, slot, file };
    },
    applyCropSelection({ file, crop, preview }) {
      const selection = this.cropSelection;
      if (!selection || selection.file !== file) {
        URL.revokeObjectURL(preview);
        return;
      }
      if (selection.slot) {
        this.applyComplementaryImage(selection.product, selection.slot, file, crop, preview);
      } else {
        const draft = this.ensureDraft(selection.product);
        this.revokeDraftPreview(selection.product.id);
        draft.image_file = file;
        draft.image_crop = crop;
        draft.image_preview = preview;
        draft.remover_imagem = false;
      }
      this.cropSelection = null;
    },
    applyComplementaryImage(product, slot, file, crop, preview) {
      const draft = this.ensureDraft(product);
      const state = draft.complementary_images[String(slot)];
      this.revokeComplementaryPreview(product.id, slot);
      state.image_file = file;
      state.image_crop = crop;
      state.image_preview = preview;
      state.removed = false;
      this.syncComplementaryRemovalSlots(draft);
    },
    removeComplementaryImage(product, slot) {
      if (this.saving || this.imageValidationBusy) return;
      const draft = this.ensureDraft(product);
      const state = draft.complementary_images[String(slot)];
      this.revokeComplementaryPreview(product.id, slot);
      state.image_file = null;
      state.image_preview = "";
      state.image_crop = null;
      state.removed = Boolean(state.original_url);
      this.syncComplementaryRemovalSlots(draft);
      this.cleanDraftIfUnchanged(product);
    },
    undoComplementaryImage(product, slot) {
      if (this.saving || this.imageValidationBusy) return;
      const draft = this.draftFor(product);
      if (!draft) return;
      const state = draft.complementary_images[String(slot)];
      this.revokeComplementaryPreview(product.id, slot);
      state.image_file = null;
      state.image_preview = "";
      state.image_crop = null;
      state.removed = false;
      this.syncComplementaryRemovalSlots(draft);
      this.cleanDraftIfUnchanged(product);
    },
    syncComplementaryRemovalSlots(draft) {
      draft.remover_imagens_complementares = COMPLEMENTARY_SLOTS.filter(
        (slot) => {
          const state = draft.complementary_images[String(slot)];
          return Boolean(state && state.removed && !state.image_file);
        }
      );
    },
    removeProductImage(product) {
      if (this.saving || this.imageValidationBusy) return;
      const draft = this.ensureDraft(product);
      this.revokeDraftPreview(product.id);
      draft.image_file = null;
      draft.image_preview = "";
      draft.image_crop = null;
      draft.remover_imagem = shouldRemoveCatalogMainImage(draft.original_image);
      this.cleanDraftIfUnchanged(product);
    },
    undoProductImage(product) {
      if (this.saving || this.imageValidationBusy) return;
      const draft = this.draftFor(product);
      if (!draft) return;
      this.revokeDraftPreview(product.id);
      draft.image_file = null;
      draft.image_preview = "";
      draft.image_crop = null;
      draft.remover_imagem = false;
      this.cleanDraftIfUnchanged(product);
    },
    revokeDraftPreview(id) {
      const draft = this.drafts[String(id)];
      if (
        draft &&
        draft.image_preview &&
        draft.image_preview.indexOf("blob:") === 0
      ) {
        URL.revokeObjectURL(draft.image_preview);
      }
      if (draft) draft.image_preview = "";
    },
    revokeComplementaryPreview(id, slot) {
      const draft = this.drafts[String(id)];
      const state =
        draft &&
        draft.complementary_images &&
        draft.complementary_images[String(slot)];
      if (
        state &&
        state.image_preview &&
        state.image_preview.indexOf("blob:") === 0
      ) {
        URL.revokeObjectURL(state.image_preview);
      }
      if (state) state.image_preview = "";
    },
    revokeAllDraftPreviews(id) {
      this.revokeDraftPreview(id);
      COMPLEMENTARY_SLOTS.forEach((slot) =>
        this.revokeComplementaryPreview(id, slot)
      );
    },
    cleanDraftIfUnchanged(product) {
      const key = String(product.id);
      const draft = this.drafts[key];
      if (!draft) return;
      const flagChanged =
        Boolean(draft.catalogo_digital) !==
        Boolean(draft.original_catalogo_digital);
      const imageChanged = Boolean(draft.image_file || draft.remover_imagem);
      const complementaryImageChanged = COMPLEMENTARY_SLOTS.some((slot) => {
        const state = draft.complementary_images[String(slot)];
        return Boolean(state && (state.image_file || state.removed));
      });
      const priceChanged = this.draftPriceChanged(draft);
      if (
        !flagChanged &&
        !imageChanged &&
        !complementaryImageChanged &&
        !priceChanged &&
        !draft.preco_erro
      ) {
        this.$delete(this.drafts, key);
      }
    },
    async saveAndPublish() {
      if (this.saving) return;
      if (this.imageValidationBusy) {
        this.$toast.error("Aguarde a validação da imagem antes de publicar.");
        return;
      }
      const dirtyDrafts = Object.keys(this.drafts).map((id) => this.drafts[id]);
      if (dirtyDrafts.length > 100) {
        this.$toast.error(
          "Publique até 100 alterações por vez para manter a operação segura."
        );
        return;
      }

      const missingCrop = dirtyDrafts.some((draft) => {
        if (draft.image_file && !draft.image_crop) return true;
        return COMPLEMENTARY_SLOTS.some((slot) => {
          const image = draft.complementary_images[String(slot)];
          return Boolean(image && image.image_file && !image.image_crop);
        });
      });
      if (missingCrop) {
        this.$toast.error("Ajuste o enquadramento das fotos antes de publicar.");
        return;
      }

      const invalidPriceDraft = dirtyDrafts.find(
        (draft) =>
          this.draftPriceChanged(draft) && !this.validateProductPrice(draft)
      );
      if (invalidPriceDraft) {
        this.$toast.error("Revise os preços destacados antes de publicar.");
        return;
      }

      this.saving = true;
      const payload = new FormData();
      const items = dirtyDrafts.map(buildCatalogProductPatch);
      payload.append("itens", JSON.stringify(items));
      dirtyDrafts.forEach((draft) => {
        if (draft.image_file) {
          payload.append(`imagens[${draft.id}]`, draft.image_file);
          Object.keys(draft.image_crop).forEach((key) =>
            payload.append(`crops[${draft.id}][${key}]`, draft.image_crop[key])
          );
        }
        COMPLEMENTARY_SLOTS.forEach((slot) => {
          const state = draft.complementary_images[String(slot)];
          if (state && state.image_file) {
            payload.append(
              `imagens_complementares[${draft.id}][${slot}]`,
              state.image_file
            );
            Object.keys(state.image_crop).forEach((key) =>
              payload.append(`crops_complementares[${draft.id}][${slot}][${key}]`, state.image_crop[key])
            );
          }
        });
      });

      try {
        const data = await publishCatalogProducts(payload);
        Object.keys(this.drafts).forEach((id) =>
          this.revokeAllDraftPreviews(id)
        );
        this.drafts = {};
        this.$toast.success("Produtos salvos e catálogo publicado.");
        this.$emit("published", data || {});
        await this.loadProducts();
      } catch (error) {
        this.notifyError(error, "Não foi possível publicar os produtos.");
      } finally {
        this.saving = false;
        if (this.searchInput.trim() !== this.appliedSearch) {
          this.applySearch();
        }
      }
    },
    editProduct(product) {
      if (this.saving || this.imageValidationBusy) return;
      if (
        this.hasDrafts &&
        !window.confirm(
          "Existem alterações não publicadas. Deseja descartá-las e abrir o cadastro deste produto?"
        )
      ) {
        return;
      }
      this.allowRouteLeave = this.hasDrafts;
      this.$router.push({
        name: "produtosUpdate",
        params: { id_produto: product.id },
      });
    },
    consumeRouteLeaveApproval() {
      const approved = this.allowRouteLeave;
      this.allowRouteLeave = false;
      return approved;
    },
    formatMoney(value) {
      return Number(value || 0).toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
      });
    },
    notifyError(error, fallback) {
      const message =
        (error && error.response && error.response.data.message) ||
        (error && error.message) ||
        fallback;
      this.$toast.error(message || fallback);
    },
  },
};
